<template>
  <base-modal
    :ref="modal.key"
    :show="open"
    :title="modal.title"
    @on-confirm="save"
    @on-cancel="hide()"
    :okText="$t('Save')"
    :closeText="$t('Close')"
    maxWidth="60%"
  >
    <div v-if="loading" class="text-center mt-4">
      <v-progress-circular
        indeterminate
        color="primary"
        size="48"
      ></v-progress-circular>
    </div>
    <div v-if="!loading">
      <v-row v-for="(user, index) in users" :key="index">
        <v-col cols="12" md="1">
          <v-btn large @click="remove(index)" icon color="red darken-4">
            <v-icon>mdi-window-close</v-icon>
          </v-btn>
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="user.name"
            v-bind:label="$t('FirstName')"
            :error-messages="nameErrors(index)"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="3">
          <v-text-field
            v-model="user.surname"
            v-bind:label="$t('Surname')"
            :error-messages="surnameErrors(index)"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-text-field
            v-model="user.email"
            v-bind:label="$t('Email')"
            :error-messages="emailErrors(index)"
          ></v-text-field>
        </v-col>
        <v-col cols="12" md="2">
          <v-select
            multiple
            v-bind:label="$t('Role')"
            item-text="text"
            item-value="text"
            v-model="user.roles"
            :items="roles"
          ></v-select>
        </v-col>
        <v-col cols="12" md="1">
          <v-btn large color="blue darken-4" icon @click="add">
            <v-icon>mdi-plus</v-icon>
          </v-btn>
        </v-col>
      </v-row>
    </div>

    <template v-slot:actions>
      <div style="position: absolute; bottom: 10px; left: 25px">
        <v-tooltip top>
          <template v-slot:activator="{ on }">
            <div v-on="on">
              <v-file-input
                @change="onCsvImport"
                loading="primary"
                accept=".csv"
                prepend-icon="mdi-file-import-outline"
                hide-input
                v-model="selectedFile"
              ></v-file-input>
            </div>
          </template>
          <span
            >{{ $t("ImportFromFile") + " (CSV):" }} <br />
            Jonh, Doe, jdoe@mail.com, admin<br />
            Jane, Doe, jadoe@mail.com, operator</span
          >
        </v-tooltip>
      </div>
    </template>
  </base-modal>
</template>
<script>
import UserService from '@/services/UserService';
import RoleService from '@/services/RoleService';
import User from '@/ecm/models/UserImport';
import Utils from '@/ecm/common/Utils';
import { required, email } from 'vuelidate/lib/validators';
import { validationMixin } from 'vuelidate';

export default {
    name: 'UserImportModal',
    created() {
        RoleService.getList(false).then((data) => {
            this.roles = data;
        });
        this.$v.$reset();
    },
    data() {
        return {
            roles: [],
            users: [],
            modal: {
                title: `${this.$t('Users')} - ${this.$t('Import')}`,
                key: 'userImportModal',
            },
            open: false,
            loading: false,
            selectedFile: null
        };
    },
    mixins: [validationMixin],
    validations: {
        users: {
            $each: {
                name: { required },
                surname: { required },
                email: { required, email },
            },
        },
    },
    computed: {
        modalInstance() {
            return this.$refs[this.modal.key];
        },
    },
    methods: {
        nameErrors(index) {
            const errors = [];
            if (!this.$v.users.$each[index].name.$dirty) return errors;
            !this.$v.users.$each[index].name.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        surnameErrors(index) {
            const errors = [];
            if (!this.$v.users.$each[index].surname.$dirty) return errors;
            !this.$v.users.$each[index].surname.required &&
                errors.push(this.$t('RequiredField'));
            return errors;
        },
        emailErrors(index) {
            const errors = [];
            if (!this.$v.users.$each[index].email.$dirty) return errors;
            !this.$v.users.$each[index].email.required &&
                errors.push(this.$t('RequiredField'));
            !this.$v.users.$each[index].email.email &&
                errors.push(this.$t('InvalidEmail'));
            return errors;
        },
        save() {
            this.$v.$touch();

            if (this.$v.$invalid) {
                return;
            }

            this.modalInstance.progress(true);
            var userImport = this.users.map(function (obj) {
                return obj.toDto();
            });
            UserService.import(userImport)
                .then((result) => {
                    if (this.users.length > 1) {
                        Utils.toast.success(
                            this.$t('Users') + ' ' + this.$t('CreatedSuccessfullyPlural')
                        );
                    } else {
                        Utils.toast.success(
                            this.$t('User') + ' ' + this.$t('UpdatedSuccessfully')
                        );
                    }
                    this.$emit('on-imported');
                    this.hide();
                })
                .catch(() => {
                    // error
                })
                .then(() => {
                    //always
                    this.modalInstance.progress(false);
                });
        },
        remove(index) {
            this.users.splice(index, 1);
            if (this.users.length == 0) {
                this.add();
            }
        },
        add() {
            this.users.push(new User());
        },
        onCsvImport(file) {
            this.loading = true;
            if (file) {
                this.selectedFile = file;
                const reader = new FileReader();
                var self = this;
                reader.onload = function () {
                    if (reader.result) {
                        try {
                            var importData = reader.result.split('\n').map(line => line.trim());
                            self.users = importData.map(function (dto) {
                                return new User(dto);
                            });
                        } catch (error) {
                            Utils.toast.error(self.$t('UnsupportedFileFormatException'));
                        } finally {
                            self.loading = false;
                        }
                    } else {
                        self.loading = false;
                    }
                };
                reader.readAsText(this.selectedFile);
            } else {
                this.users = [];
                this.loading = false;
            }
        },
        new() {
            this.users = [new User()];
            this.show();
        },
        show() {
            this.open = true;
        },
        hide() {
            this.open = false;
            this.selectedFile = null;
        },
    },
};
</script>
